import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import { AccountService } from './core/account.service';
import { AuthService } from './core/auth.service';
import { LanguageService } from './core/language';
import { PresenceService } from './core/presence.service';
import { formatMoney, formatPct, formatPrice } from './core/format';
import { MarketService } from './core/market.service';
import { ToastService } from './core/toast.service';
import { DeskAssistant } from './ui/desk-assistant';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, DeskAssistant],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  protected readonly auth = inject(AuthService);
  protected readonly lang = inject(LanguageService);
  protected readonly market = inject(MarketService);
  protected readonly toast = inject(ToastService);
  private readonly account = inject(AccountService);
  private readonly router = inject(Router);
  private readonly presence = inject(PresenceService);
  protected readonly menuOpen = signal(false);
  protected readonly touchOpen = signal(false);
  protected readonly openDrop = signal<string | null>(null);
  protected readonly searchQuery = signal('');
  protected readonly searchOpen = signal(false);
  protected readonly dropTop = signal(120);
  protected readonly dropLeft = signal(24);
  private dropTimer = 0;
  protected readonly onCrm = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url.startsWith('/crm')),
    ),
    { initialValue: this.router.url.startsWith('/crm') },
  );
  protected readonly money = formatMoney;

  protected year(): number {
    return new Date().getFullYear();
  }
  protected readonly price = formatPrice;
  protected readonly pct = formatPct;

  protected tape() {
    const quotes = this.market.quotes();
    return [...quotes, ...quotes];
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
    this.openDrop.set(null);
  }

  protected initials(): string {
    const name = this.auth.user()?.name?.trim() ?? '';
    const parts = name.split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '·';
    }
    const first = parts[0][0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
    return (first + last).toUpperCase();
  }

  protected toggleDrop(id: string, event: Event): void {
    this.placeDrop(event);
    const desktop = !window.matchMedia('(max-width: 1180px)').matches;
    if (desktop && this.openDrop() === id) {
      return;
    }
    this.openDrop.update((current) => (current === id ? null : id));
  }

  protected hoverDrop(id: string, event: Event): void {
    if (window.matchMedia('(max-width: 1180px)').matches) {
      return;
    }
    window.clearTimeout(this.dropTimer);
    this.placeDrop(event);
    this.openDrop.set(id);
  }

  private placeDrop(event: Event): void {
    const host = event.currentTarget as HTMLElement;
    const rect = host.getBoundingClientRect();
    const nav = host.closest('.nav');
    const bottom = nav?.getBoundingClientRect().bottom ?? rect.bottom;
    this.dropTop.set(bottom + 10);
    this.dropLeft.set(Math.max(16, Math.min(rect.left, window.innerWidth - 356)));
  }

  protected leaveDrop(): void {
    if (window.matchMedia('(max-width: 1180px)').matches) {
      return;
    }
    this.dropTimer = window.setTimeout(() => this.openDrop.set(null), 160);
  }

  protected stayDrop(): void {
    window.clearTimeout(this.dropTimer);
  }

  protected openTouch(): void {
    this.closeMenu();
    this.touchOpen.set(true);
  }

  protected closeTouch(): void {
    this.touchOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.searchOpen()) {
      this.searchOpen.set(false);
      return;
    }
    if (this.openDrop()) {
      this.openDrop.set(null);
      return;
    }
    if (this.touchOpen()) {
      this.closeTouch();
    }
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.nav-drop, .nav-account')) {
      this.openDrop.set(null);
    }
    if (!target?.closest('.nav-search')) {
      this.searchOpen.set(false);
    }
  }

  protected readonly matches = computed(() => {
    const needle = this.searchQuery().trim().toLowerCase();
    const quotes = this.market.quotes();
    if (!needle) {
      return quotes;
    }
    return quotes.filter((quote) =>
      quote.symbol.toLowerCase().includes(needle) || quote.name.toLowerCase().includes(needle),
    );
  });

  protected onSearchInput(value: string): void {
    this.searchQuery.set(value);
    this.searchOpen.set(true);
  }

  protected openSearch(): void {
    this.searchOpen.set(true);
  }

  protected pickSymbol(symbol: string, input?: HTMLInputElement): void {
    this.searchOpen.set(false);
    this.searchQuery.set('');
    if (input) {
      input.value = '';
    }
    this.closeMenu();
    if (this.auth.signedIn()) {
      void this.router.navigate(['/portfolio'], { queryParams: { symbol } });
      return;
    }
    void this.router.navigate(['/trade', symbol]);
  }

  protected onSearch(event: Event, input: HTMLInputElement): void {
    event.preventDefault();
    const hit = this.bestMatch(input.value);
    if (hit) {
      this.pickSymbol(hit.symbol, input);
      return;
    }
    const query = input.value.trim();
    if (!query) {
      return;
    }
    this.searchOpen.set(false);
    this.closeMenu();
    void this.router.navigate(['/markets'], { queryParams: { q: query } });
  }

  private bestMatch(raw: string) {
    const needle = raw.trim().toLowerCase();
    if (!needle) {
      return undefined;
    }
    const quotes = this.market.quotes();
    return quotes.find((quote) => quote.symbol.toLowerCase() === needle || quote.name.toLowerCase() === needle)
      ?? quotes.find((quote) => quote.symbol.toLowerCase().startsWith(needle) || quote.name.toLowerCase().startsWith(needle))
      ?? quotes.find((quote) => quote.symbol.toLowerCase().includes(needle) || quote.name.toLowerCase().includes(needle));
  }

  protected signOut(): void {
    this.auth.signOut();
    this.account.clear();
    this.closeMenu();
    void this.router.navigateByUrl('/');
  }
}
